<script setup lang="ts">
import { RouterView } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { onMounted } from 'vue';
import '@/assets/main.css';
import posthog from 'posthog-js';
import SessionExpiredModal from '@/components/modals/SessionExpiredModal.vue';
import TransitionWrapper from '@/components/TransitionWrapper.vue';
import UseDesktopBanner from '@/components/warning_banners/UseDesktopBanner.vue';
import { useUiControlStore } from '@/stores/ui-control';
import { storeToRefs } from 'pinia';
import { initGoogleAnalytics } from '@/helpers/googleAnalytics';
import { captureFirstTouchAttribution } from '@/helpers/attribution';

const isDev = import.meta.env.VITE_FEATURE_IS_DEV === 'true';

const userStore = useUserStore();
const uiControlStore = useUiControlStore();
const { allClearToDisplayDashboard } = storeToRefs(uiControlStore);

onMounted(() => {
	void captureFirstTouchAttribution();
	initGoogleAnalytics();

	const posthogApiKey = import.meta.env.VITE_POSTHOG_API_KEY;
	if (posthogApiKey) {
		posthog.init(posthogApiKey, {
			api_host: 'https://ph.qweebi.com',
			ui_host: 'https://app.posthog.com',
			capture_pageleave: false,
			disable_session_recording: isDev || import.meta.env.VITE_MODE === 'development',
			before_send: (event) => {
				if (event === null) {
					return null;
				}
				if (isDev || import.meta.env.VITE_MODE === 'development') {
					return null;
				}
				if (event.event === '$feature_flag_called') {
					return null;
				}
				return event;
			},
		});
	}

	if (userStore.user) {
		const userEmail = userStore.user.email;
		posthog.identify(userStore.user.id, {
			environment: import.meta.env.VITE_MODE,
			email: userEmail,
			name: userStore.user.name,
			kyc_details: userStore.user.profile,
		});

		const internalUserDomains = [/qweebi\.com/i, /example\.com/i, /mailinator\.com/i];
		if (internalUserDomains.some((domain) => domain.test(userEmail))) {
			posthog.stopSessionRecording();
		}
	}
});
</script>

<template>
	<div class="flex min-h-screen flex-col max-sm:min-h-dvh">
		<TransitionWrapper name="page" type="fade" mode="out-in">
			<div class="grid grid-cols-1 grid-rows-1">
				<RouterView class="col-span-1 col-start-1 row-span-1 row-start-1" />
				<div v-if="!allClearToDisplayDashboard" class="col-span-1 col-start-1 row-span-1 row-start-1 bg-neutral-700" />
			</div>
		</TransitionWrapper>
		<notifications class="absolute mt-10 mr-20 rounded-sm" />
		<UseDesktopBanner />
		<SessionExpiredModal />
	</div>
</template>
